import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// SSR remains authoritative: fetch another Turbo frame, never a client-side
// conversation model. Reply/contact forms live outside the replaceable frame.
export class SmsPollController extends Controller {
    static targets = ["frame", "notice"]
    static values = { url: String, enabled: { type: Boolean, default: true }, inbox: Boolean }

    connect() {
        this.failures = 0
        this.stopped = false
        this.visibilityChanged = () => {
            this.clearTimer()
            if (!document.hidden) { this.acknowledge(); this.schedule() }
        }
        this.beforeCache = () => this.disconnect()
        document.addEventListener("visibilitychange", this.visibilityChanged)
        document.addEventListener("turbo:before-cache", this.beforeCache)
        const scroller = this.scroller()
        if (scroller && this.enabledValue) scroller.scrollTop = scroller.scrollHeight
        this.acknowledge()
        this.schedule()
    }

    disconnect() {
        this.stopped = true
        this.clearTimer()
        this.abortController?.abort()
        document.removeEventListener("visibilitychange", this.visibilityChanged)
        document.removeEventListener("turbo:before-cache", this.beforeCache)
    }

    clearTimer() { clearTimeout(this.timer) }
    scroller() { return this.element.querySelector('[data-sms-poll-target~="scroller"]') }
    nearBottom(scroller) { return !scroller || scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 48 }

    schedule() {
        this.clearTimer()
        if (this.stopped || document.hidden || !this.enabledValue) return
        this.timer = setTimeout(() => this.refresh(), Math.min(60000, 10000 * (2 ** this.failures)))
    }

    async refresh(event) {
        event?.preventDefault()
        const manual = Boolean(event)
        if (this.stopped || this.busy || document.hidden) return
        const oldScroller = this.scroller()
        const bottom = this.nearBottom(oldScroller)
        if (!manual && (!this.enabledValue || !bottom)) {
            this.say("Live updates paused while reading older messages.")
            this.schedule()
            return
        }
        const scrollTop = oldScroller?.scrollTop ?? 0
        this.busy = true
        this.clearTimer()
        this.abortController = new AbortController()
        const timeout = setTimeout(() => this.abortController.abort(), 15000)
        try {
            const response = await fetch(this.urlValue, {
                credentials: "same-origin", cache: "no-store", signal: this.abortController.signal,
                headers: { Accept: "text/html", "Turbo-Frame": this.frameTarget.id }
            })
            if (response.redirected || response.status === 401 || response.status === 403) {
                this.stopForAuth()
                return
            }
            if (!response.ok) throw new Error("Refresh failed")
            const html = await response.text()
            if (this.stopped || document.hidden) return
            // The user may have started reading while the request was in
            // flight. Preserve that newer scroll position, not the old one.
            const scrollerBeforeRender = this.scroller()
            const followBottom = this.nearBottom(scrollerBeforeRender)
            const position = scrollerBeforeRender?.scrollTop ?? scrollTop
            if (!manual && !followBottom) {
                this.say("Live updates paused while reading older messages.")
                return
            }
            const page = new DOMParser().parseFromString(html, "text/html")
            const frame = page.getElementById(this.frameTarget.id)
            if (!frame || frame.tagName !== "TURBO-FRAME") throw new Error("Missing frame")
            const stream = document.createElement("turbo-stream")
            stream.setAttribute("action", "replace")
            stream.setAttribute("target", this.frameTarget.id)
            const template = document.createElement("template")
            template.content.appendChild(document.importNode(frame, true))
            stream.appendChild(template)
            const focus = this.captureInboxFocus()
            Turbo.renderStreamMessage(stream.outerHTML)
            // Turbo renders on the next animation frame. Stay connected to this
            // controller's parent; only its child frame is replaced.
            await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
            if (this.stopped) return
            this.restoreInboxFocus(focus)
            const currentScroller = this.scroller()
            if (currentScroller) currentScroller.scrollTop = followBottom ? currentScroller.scrollHeight : position
            this.failures = 0
            this.say(this.enabledValue ? "Up to date" : "Viewing older messages")
            await this.acknowledge()
        } catch (error) {
            if (!this.stopped) {
                this.failures = Math.min(this.failures + 1, 3)
                this.say(this.inboxValue ? "Unable to refresh the inbox; retrying shortly." : "Unable to refresh. Your draft is still here; retrying shortly.")
            }
        } finally {
            clearTimeout(timeout)
            this.busy = false
            this.schedule()
        }
    }

    captureInboxFocus() {
        if (!this.inboxValue || !this.frameTarget.contains(document.activeElement)) return null
        const element = document.activeElement
        const rows = [...this.frameTarget.querySelectorAll('[data-sms-inbox-row]')]
        return { element, id: element.id, rowIndex: rows.indexOf(element),
            href: element.tagName === "A" ? element.getAttribute("href") : null,
            label: element.tagName === "BUTTON" ? element.textContent.trim() : null }
    }

    restoreInboxFocus(focus) {
        if (!focus || focus.element.isConnected ||
            (document.activeElement && document.activeElement !== document.body && document.activeElement !== focus.element)) return
        const frame = this.frameTarget
        const rows = [...frame.querySelectorAll('[data-sms-inbox-row]')]
        const same = focus.id ? document.getElementById(focus.id) : null
        const control = [...frame.querySelectorAll('a, button')].find(element =>
            (focus.href && element.getAttribute("href") === focus.href) ||
            (focus.label && element.tagName === "BUTTON" && !element.disabled && element.textContent.trim() === focus.label))
        const target = (same && frame.contains(same) ? same : null) || control ||
            rows[Math.min(Math.max(0, focus.rowIndex), rows.length - 1)] || frame.querySelector('#sms-inbox-empty')
        target?.focus({ preventScroll: true })
    }

    async acknowledge() {
        if (this.stopped || document.hidden || !this.enabledValue || !this.nearBottom(this.scroller())) return
        const form = this.element.querySelector('[data-sms-poll-target~="readForm"]')
        if (!form) return
        const data = new FormData(form)
        const watermark = data.get("watermark")
        if (watermark === this.lastRead || this.readBusy) return
        this.readBusy = true
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 10000)
        try {
            const response = await fetch(form.action, {
                method: "POST", body: data, credentials: "same-origin", cache: "no-store",
                signal: controller.signal, headers: { "X-SMS-Poll": "1", Accept: "text/plain" }
            })
            if (response.redirected || response.status === 401 || response.status === 403) this.stopForAuth()
            else if (response.status === 204) this.lastRead = watermark
        } catch (error) {
            // Manual mark-read remains available; never discard a draft.
        } finally {
            clearTimeout(timeout)
            this.readBusy = false
        }
    }

    say(message) { if (this.hasNoticeTarget) this.noticeTarget.textContent = message }
    stopForAuth() {
        this.stopped = true
        this.clearTimer()
        this.say(this.inboxValue ? "Your session expired or access changed. Sign in again to refresh the inbox."
            : "Your session expired or access changed. Copy your draft before logging in again.")
    }
}
